/**
 * @license
 * @copyright Copyright Motili Inc., 2025 All Rights Reserved
 */

import { useState, useMemo, useEffect } from 'react';
import { capitalize, flatMap } from 'lodash';
import { DropDownSectionedSelect, DropDownSelectOption } from '@motili/ui';

import Drawer from 'common/components-v2/modal/Drawer';
import MotiliButton from 'common/components-v2/buttons/MotiliButton';
import MotiliButtonContainer from 'common/components-v2/buttons/MotiliButtonContainer';
import { useWorkOrderRequirementApprovalReasonsQuery } from 'common/stores/redux/MetaStore/MetaStoreApi';
import { FormElement, FormRow } from 'common/components/form/FormLayout';
import FormTextArea from 'common/components/form/FormTextArea';
import FormManufacturerYearInput from 'common/components/form/FormManufacturerYearInput';
import * as logger from 'common/utils/Logger';
import Alert from 'common/components/Alert';
import * as Validator from 'common/utils/Validator';
import RadioGroup from 'common/components/form/RadioGroup';
import CheckboxGroup from 'common/components/form/CheckboxGroup';
import { Photo, PhotoApproval } from 'common/types/documentRequirements';
import styled from 'styled-components';

const BodyWrap = styled.div`
    padding: 0;
`;

const FullWidthField = styled.div`
    width: 100%;
    label {
        white-space: nowrap;
    }
    input {
        width: 100%;
        box-sizing: border-box;
    }
`;

interface PhotoRequirementsAcceptModalProps {
    photo: Photo;
    handleClose: () => void;
    handleSave: (id: number, approval: PhotoApproval) => Promise<void>;
    clearHistory: () => void;
    initialType?: string;
}

const PhotoRequirementsAcceptModal = ({
    photo,
    handleClose,
    handleSave,
    clearHistory,
    initialType,
}: PhotoRequirementsAcceptModalProps) => {
    const [busy, setBusy] = useState(false);
    const [changeType, setChangeType] = useState(initialType || '');
    const [changeReason, setChangeReason] = useState('');
    const [changeReasonOther, setChangeReasonOther] = useState('');
    const [reasons, setReasons] = useState<string[]>([]);
    const [additionalReasons, setAdditionalReasons] = useState<
        DropDownSelectOption<string>[]
    >([]);
    const [manufactureYear, setManufactureYear] = useState<number | ''>('');
    const [description, setDescription] = useState('');
    const [reasonOther, setReasonOther] = useState('');

    const editMode = !!photo.approvalStatusId;
    const typeChanged = !editMode || initialType !== changeType;

    const otherSelected = (options: string[]) => {
        if (options) {
            return options.find(reason => reason === 'OTHER');
        }
        return false;
    };

    const saveDisabled = () => {
        return (
            reasons.length <= 0 ||
            (otherSelected(reasons) && !reasonOther) ||
            (editMode && !changeReason) ||
            (otherSelected([changeReason]) && !changeReasonOther) ||
            (manufactureYear &&
                !Validator.isManufacturerYear(manufactureYear)) ||
            manufactureYear === 0
        );
    };

    const handleSubmit = async () => {
        setBusy(true);

        const approval: PhotoApproval = {
            approvalStatusId: changeType,
            reasons,
            additionalReasons: additionalReasons.map(reason => reason.value),
            additionalNotes: description,
            reasonOther,
            manufacturerYear: manufactureYear,
        };
        if (editMode) {
            approval.changeReason = changeReason;
            approval.changeReasonOther = changeReasonOther;
        }

        try {
            await handleSave(photo.id, approval);

            setBusy(false);
            clearHistory();
            handleClose();
        } catch (error: any) {
            logger.error(error);
            Alert.error(
                `Sorry, issue accept photo requirement: ${error.message}`
            );
            setBusy(false);
        }
    };

    const handleChangeTypeChanged = (change: string) => {
        setChangeType(change);
        setChangeReason('');
        setChangeReasonOther('');
        setReasons([]);
        setReasonOther('');
        setAdditionalReasons([]);
        setManufactureYear('');
        setDescription('');
    };

    const handleChangeReasonChanged = (change: string) => {
        setChangeReason(change);
        setChangeReasonOther('');
    };

    const handleReasonsChanged = (change: string[]) => {
        setReasons(reasons => {
            if (!otherSelected(reasons)) {
                setReasonOther('');
            }
            return change;
        });
    };

    const title = !editMode
        ? `Photo ${capitalize(initialType)} Reason`
        : 'Photo Edit Reason';

    return (
        <Drawer
            open
            onClose={handleClose}
            closeByDocument
            showClose={!busy}
            drawerStyle={{ width: 520 }}
            title={title}
        >
            <BodyWrap>
                <div style={{ paddingTop: 0 }}>
                    {editMode && photo.approvalStatusId && (
                        <>
                            <FormRow>
                                <FormElement>
                                    <ChangeType
                                        approvalStatusId={
                                            photo.approvalStatusId
                                        }
                                        value={changeType}
                                        onChange={handleChangeTypeChanged}
                                    />
                                </FormElement>
                            </FormRow>

                            {typeChanged && (
                                <>
                                    <FormRow>
                                        <FormElement>
                                            <ChangeReason
                                                approvalStatusId={
                                                    photo.approvalStatusId
                                                }
                                                changeType={changeType}
                                                value={changeReason}
                                                onChange={
                                                    handleChangeReasonChanged
                                                }
                                            />
                                        </FormElement>
                                    </FormRow>

                                    {otherSelected([changeReason]) && (
                                        <FormRow>
                                            <FormElement>
                                                <FormTextArea
                                                    controlId='change-reason-other'
                                                    label='OTHER REASON'
                                                    value={changeReasonOther}
                                                    onChange={event =>
                                                        setChangeReasonOther(
                                                            event.target.value
                                                        )
                                                    }
                                                    inputStyle={{
                                                        minHeight: '80px',
                                                    }}
                                                    disabled={busy}
                                                    required
                                                />
                                            </FormElement>
                                        </FormRow>
                                    )}
                                </>
                            )}
                        </>
                    )}

                    {typeChanged && (
                        <ApprovalReasonContent
                            busy={busy}
                            approvalType={changeType}
                            manufactureYear={manufactureYear}
                            setManufactureYear={setManufactureYear}
                            reasonSelected={reasons}
                            handleReasonsChanged={handleReasonsChanged}
                            reasonOther={reasonOther}
                            setReasonOther={setReasonOther}
                            additionalReasons={additionalReasons}
                            setAdditionalReasons={setAdditionalReasons}
                            additionalComments={description}
                            setAdditionalComments={setDescription}
                        />
                    )}
                </div>

                <div style={{ paddingTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
                    <MotiliButtonContainer>
                        <MotiliButton
                            text='Cancel'
                            onClick={handleClose}
                            disabled={busy}
                            moStyle='normal'
                        />
                        <MotiliButton
                            text='Save'
                            onClick={handleSubmit}
                            busy={busy}
                            disabled={saveDisabled()}
                        />
                    </MotiliButtonContainer>
                </div>
            </BodyWrap>
        </Drawer>
    );
};

export default PhotoRequirementsAcceptModal;

type ApprovalReasonContentProps = {
    busy: boolean;
    approvalType: string;
    manufactureYear: number | '';
    setManufactureYear: (year: number | '') => void;
    reasonSelected: string[];
    handleReasonsChanged: (reasons: string[]) => void;
    reasonOther: string;
    setReasonOther: (reasonOther: string) => void;
    additionalReasons: DropDownSelectOption<string>[];
    setAdditionalReasons: (
        additionalReasons: DropDownSelectOption<string>[]
    ) => void;
    additionalComments: string;
    setAdditionalComments: (additionalComments: string) => void;
};

export function ApprovalReasonContent({
    busy,
    approvalType,
    manufactureYear,
    setManufactureYear,
    reasonSelected,
    handleReasonsChanged,
    reasonOther,
    setReasonOther,
    additionalReasons,
    setAdditionalReasons,
    additionalComments,
    setAdditionalComments,
}: ApprovalReasonContentProps) {
    const otherSelected = (options: string[]) => {
        if (options) {
            return options.find(reason => reason === 'OTHER');
        }
        return false;
    };
    if (!approvalType || approvalType === '') return null;

    return (
        <>
            {approvalType === 'APPROVE' && (
                <FormRow>
                    <FormElement>
                        <FullWidthField>
                            <FormManufacturerYearInput
                                label='MANUFACTURER YEAR'
                                value={manufactureYear}
                                onChange={(e: any) => {
                                    if (
                                        e.target.value === '' ||
                                        /^[0-9]+$/.test(e.target.value)
                                    ) {
                                        const value = Number.isNaN(
                                            parseInt(e.target.value, 10)
                                        )
                                            ? ''
                                            : Number(e.target.value);
                                        setManufactureYear(value);
                                    }
                                }}
                                disabled={busy}
                            />
                        </FullWidthField>
                    </FormElement>
                </FormRow>
            )}

            <FormRow>
                <FormElement>
                    <ApprovalReasons
                        approvalStatusId={approvalType}
                        reasons={reasonSelected}
                        onChange={handleReasonsChanged}
                    />
                </FormElement>
            </FormRow>

            {otherSelected(reasonSelected) && (
                <FormRow>
                    <FormElement>
                        <FormTextArea
                            controlId='reason-other'
                            label='OTHER REASON'
                            value={reasonOther}
                            onChange={event =>
                                setReasonOther(event.target.value)
                            }
                            inputStyle={{ minHeight: '80px' }}
                            disabled={busy}
                            required
                        />
                    </FormElement>
                </FormRow>
            )}

            <AdditionalReasons
                reasons={reasonSelected}
                additionalReasons={additionalReasons}
                onChange={setAdditionalReasons}
            />

            <FormRow>
                <FormElement>
                    <FormTextArea
                        controlId='reason-description'
                        label='ADDITIONAL COMMENTS'
                        value={additionalComments}
                        onChange={event =>
                            setAdditionalComments(event.target.value)
                        }
                        inputStyle={{ minHeight: '80px' }}
                        disabled={busy}
                    />
                </FormElement>
            </FormRow>
        </>
    );
}

type ApprovalReasonsProps = {
    approvalStatusId: string;
    reasons: string[];
    onChange: (reasons: string[]) => void;
};
function ApprovalReasons({
    approvalStatusId,
    reasons,
    onChange,
}: ApprovalReasonsProps) {
    const { data: requirementReasons } =
        useWorkOrderRequirementApprovalReasonsQuery();
    const options = useMemo(
        () =>
            requirementReasons
                ?.filter(reason => reason.categories.includes(approvalStatusId))
                .map(reason => ({
                    id: reason.id,
                    display: reason.display,
                })) || [],
        [requirementReasons, approvalStatusId]
    );
    const label =
        {
            ACCEPT: 'ACCEPT REASON (SELECT 1 OR MORE)',
            APPROVE: 'APPROVAL REASON (SELECT 1 OR MORE)',
            REJECT: 'REJECTION REASON (SELECT 1 OR MORE)',
        }[approvalStatusId] || '';
    return (
        <CheckboxGroup
            name='input-reasons'
            label={label}
            value={reasons}
            onChange={onChange}
            options={options}
            required
        />
    );
}

type ChangeTypeProps = {
    approvalStatusId: string;
    value: string;
    onChange: (reason: string) => void;
};
function ChangeType({ approvalStatusId, value, onChange }: ChangeTypeProps) {
    const options = useMemo(
        () =>
            [
                { id: 'ACCEPT', display: 'Accept' },
                { id: 'APPROVE', display: 'Approve' },
                { id: 'REJECT', display: 'Reject' },
            ].filter(option => option.id !== approvalStatusId),
        [approvalStatusId]
    );
    return (
        <RadioGroup
            name='change-type'
            label='CHANGE TYPE'
            required
            value={value}
            options={options}
            onChange={onChange}
        />
    );
}

type ChangeReasonProps = {
    approvalStatusId: string;
    changeType: string;
    value: string;
    onChange: (reason: string) => void;
};
function ChangeReason({
    approvalStatusId,
    changeType,
    value,
    onChange,
}: ChangeReasonProps) {
    const { data: requirementReasons } =
        useWorkOrderRequirementApprovalReasonsQuery();
    const KEY =
        {
            ACCEPT: `EDIT_ACCEPTED_${changeType}`,
            APPROVE: `EDIT_APPROVED_${changeType}`,
            REJECT: `EDIT_REJECTED_${changeType}`,
        }[approvalStatusId] || '';
    const options = useMemo(
        () =>
            requirementReasons?.filter(reason =>
                reason.categories.includes(KEY)
            ) || [],
        [requirementReasons, KEY]
    );
    return (
        <RadioGroup
            name='change-reason'
            label='CHANGE REASON'
            required
            value={value}
            options={options}
            onChange={onChange}
        />
    );
}

type AdditionalReasonsProps = {
    reasons: string[];
    additionalReasons: DropDownSelectOption<string>[];
    onChange: (reasons: any) => void;
};
function AdditionalReasons({
    reasons,
    additionalReasons,
    onChange,
}: AdditionalReasonsProps) {
    const { data: requirementReasons } =
        useWorkOrderRequirementApprovalReasonsQuery();
    const selectedReasons = useMemo(
        () =>
            requirementReasons
                ? requirementReasons.filter(
                      reason =>
                          reason.categories.includes('REJECT') &&
                          reasons.includes(reason.id) &&
                          reason.additionalReasons
                  )
                : [],
        [requirementReasons, reasons]
    );
    const options = useMemo(() => {
        if (selectedReasons.length === 0) return [];
        return selectedReasons.map(reason => ({
            label: reason.display,
            options:
                reason.additionalReasons?.map(reason => ({
                    value: reason.id,
                    label: reason.display,
                })) || [],
        }));
    }, [selectedReasons]);

    useEffect(() => {
        const availableReasons = flatMap(
            selectedReasons,
            option => option.additionalReasons || []
        );
        const next = additionalReasons.filter(reason =>
            availableReasons.find(selected => selected.id === reason.value)
        );
        onChange(next);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedReasons]);

    if (options.length === 0) return null;
    return (
        <FormRow>
            <FormElement style={{ margin: '1px' }}>
                <DropDownSectionedSelect
                    label='ADDITIONAL REJECTION REASON'
                    name='additional-reason-label'
                    value={additionalReasons}
                    groupedOptions={options}
                    onChange={onChange}
                    isSearchable
                    isMulti
                    menuShouldScrollIntoView
                />
            </FormElement>
        </FormRow>
    );
}
